import { useState } from "react";
import { ThumbsUp, ThumbsDown, Copy, Check, Download, Mail } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { supabase } from "@/shared/integrations/supabase/client";
import { toast } from "sonner";

interface AiMessageActionsProps {
  messageId: string;
  aiResponse: string;
  userMessage?: string;
  source: string;
  userId?: string;
  contextMessages?: any[];
  isLoading?: boolean;
}

const AiMessageActions = ({
  messageId,
  aiResponse,
  userMessage = "",
  source,
  userId,
  contextMessages,
  isLoading = false,
}: AiMessageActionsProps) => {
  const [rated, setRated] = useState<"positive" | "negative" | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctionText, setCorrectionText] = useState("");

  if (isLoading) return null;

  const handleRate = async (rating: "positive" | "negative") => {
    if (rated) return;
    setRated(rating);

    if (rating === "negative") {
      setShowCorrection(true);
      return;
    }

    try {
      await (supabase as any).from("ai_calculation_feedback").insert({
        user_id: userId || "",
        message_id: messageId,
        rating,
        ai_response: aiResponse.slice(0, 5000),
        context_messages: contextMessages
          ? JSON.stringify(contextMessages.slice(-6).map((m: any) => ({ role: m.role, content: m.content?.slice(0, 500) })))
          : null,
      });
      toast.success("Obrigado pelo feedback!");
    } catch {
      toast.error("Erro ao registrar feedback.");
      setRated(null);
    }
  };

  const submitCorrection = async () => {
    const text = correctionText.trim();
    if (!text) {
      toast.error("Descreva o erro ou a correção esperada.");
      return;
    }

    try {
      await (supabase as any).from("ai_calculation_feedback").insert({
        user_id: userId || "",
        message_id: messageId,
        rating: "negative",
        correction_text: text,
        ai_response: aiResponse.slice(0, 5000),
        context_messages: contextMessages
          ? JSON.stringify(contextMessages.slice(-6).map((m: any) => ({ role: m.role, content: m.content?.slice(0, 500) })))
          : null,
      });
      toast.success("Correção registrada! A IA será aprimorada.");
      setShowCorrection(false);
      setCorrectionText("");
    } catch {
      toast.error("Erro ao salvar correção.");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(aiResponse);
      setCopied(true);
      toast.success("Resposta copiada!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Erro ao copiar.");
    }
  };

  const handleDownload = () => {
    const blob = new Blob([aiResponse], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resposta-ia-${source}-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Resposta exportada!");
  };

  const handleEmail = () => {
    const subject = `Resposta IA – ${source} – ${new Date().toLocaleDateString("pt-BR")}`;
    const body = [
      userMessage ? `Pergunta: ${userMessage}` : "",
      "",
      "Resposta da IA:",
      aiResponse.slice(0, 1500),
      aiResponse.length > 1500 ? "\n[...resposta truncada — exporte o arquivo completo e anexe ao e-mail]" : "",
    ].filter(Boolean).join("\n");
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };

  return (
    <div className="ml-8 mt-1 space-y-2">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${rated === "positive" ? "text-green-500" : "text-muted-foreground hover:text-green-500"}`}
          onClick={() => handleRate("positive")}
          disabled={!!rated}
          title="Resposta útil"
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${rated === "negative" ? "text-destructive" : "text-muted-foreground hover:text-destructive"}`}
          onClick={() => handleRate("negative")}
          disabled={rated === "positive"}
          title="Resposta pode melhorar"
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${copied ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
          onClick={handleCopy}
          title="Copiar resposta"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-primary"
          onClick={handleDownload}
          title="Exportar resposta"
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-primary"
          onClick={handleEmail}
          title="Enviar por e-mail"
        >
          <Mail className="h-3.5 w-3.5" />
        </Button>
      </div>

      {showCorrection && (
        <div className="space-y-2 border rounded-lg p-3 bg-background">
          <p className="text-xs font-medium text-muted-foreground">Descreva o erro ou informe o resultado correto:</p>
          <Textarea
            id="correcao-ia"
            name="correcao"
            placeholder="Ex: O cálculo deveria considerar..."
            value={correctionText}
            onChange={(e) => setCorrectionText(e.target.value)}
            className="min-h-[70px] text-xs"
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setShowCorrection(false); setRated(null); }} className="text-xs">
              Cancelar
            </Button>
            <Button size="sm" onClick={submitCorrection} className="text-xs">
              Enviar correção
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiMessageActions;
